// Portfolio Administration v1.0 PR 5 — cross-portfolio Vendors registry
// list page at /portfolio-admin/vendors. Replaces the PR 0 placeholder.
// The old ?view=vfi-dashboard sub-view retired with Vendor Program v2
// PR 3 — its tiles live on the VFI Accounting page, so the legacy param
// 301-redirects there. Behind PortfolioAdminV1; falls back to the PR 0
// placeholder when the flag is off so rollouts stay reversible.

import { useCallback, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useVendorsList } from "@/hooks/portfolioAdmin/useRegistries";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import { RegistryListLayout } from "@/components/portfolioAdmin/registry/RegistryListLayout";
import { VendorsListFilters } from "@/components/portfolioAdmin/vendors/VendorsListFilters";
import { VendorsListTable } from "@/components/portfolioAdmin/vendors/VendorsListTable";
import { CreateVendorModal } from "@/components/portfolioAdmin/vendors/CreateVendorModal";

function arrayParam(params: URLSearchParams, key: string): string[] {
  const raw = params.getAll(key);
  if (raw.length === 0) return [];
  // Support both repeated (?key=a&key=b) and comma-joined (?key=a,b) forms.
  return raw.flatMap((v) => v.split(",")).filter(Boolean);
}

export default function VendorsListPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Vendors"
        description="Required vendors, VFI ledger, MNCA, VVCA."
      />
    );
  }
  return <VendorsListPageInner />;
}

function VendorsListPageInner() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const types = arrayParam(params, "type");
  const statuses = arrayParam(params, "status");
  const search = params.get("search") ?? "";
  const showArchived = params.get("archived") === "true";
  const [modalOpen, setModalOpen] = useState(false);

  const canAdd =
    auth.hasRoleCode("E1-VM") ||
    auth.hasRoleCode("E5") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Vendors.Manage");

  const writeParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setMultiParam = (key: string) => (values: string[]) =>
    writeParams((p) => {
      p.delete(key);
      values.forEach((v) => p.append(key, v));
    });

  const setSearch = (next: string) =>
    writeParams((p) => {
      if (next) p.set("search", next);
      else p.delete("search");
    });

  const setShowArchived = (next: boolean) =>
    writeParams((p) => {
      if (next) p.set("archived", "true");
      else p.delete("archived");
    });

  const clearFilters = () =>
    writeParams((p) => {
      p.delete("type");
      p.delete("status");
      p.delete("search");
      p.delete("archived");
    });

  const listQuery = useVendorsList({
    types: types.length ? types : undefined,
    statuses: statuses.length ? statuses : undefined,
    search: search || undefined,
    includeArchived: showArchived || undefined,
  });

  const vendors = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const countSummary = listQuery.isLoading
    ? "Loading vendors…"
    : `Showing ${vendors.length} ${vendors.length === 1 ? "vendor" : "vendors"}`;

  // Legacy ?view=vfi-dashboard deep links land on the VFI Accounting page.
  if (params.get("view") === "vfi-dashboard") {
    return <Navigate to="/portfolio-admin/vfi-accounting" replace />;
  }

  return (
    <>
      <RegistryListLayout
        title="Vendors"
        testIdPrefix="vendors"
        actions={
          canAdd ? (
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="vendors-add-button"
            >
              <Plus size={16} aria-hidden="true" /> Add vendor
            </Button>
          ) : undefined
        }
        filters={
          <VendorsListFilters
            types={types}
            statuses={statuses}
            search={search}
            showArchived={showArchived}
            onTypesChange={setMultiParam("type")}
            onStatusesChange={setMultiParam("status")}
            onSearchChange={setSearch}
            onShowArchivedChange={setShowArchived}
            onClear={clearFilters}
          />
        }
        countSummary={countSummary}
      >
        <VendorsListTable vendors={vendors} />
      </RegistryListLayout>
      <CreateVendorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
